import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function PaymentResult({ success }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState(success ? "checking" : "cancelled");

  useEffect(() => {
    if (!success || !sessionId) return;
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { await refresh(); return !stop && setState("paid"); }
        if (data.status === "expired") return !stop && setState("failed");
      } catch (e) { /* keep polling */ }
      if (++tries >= 15) return !stop && setState("timeout");
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [success, sessionId, refresh]);

  const view = {
    checking: [Loader2, "Confirming your payment…", "This only takes a few seconds.", "text-primary animate-spin"],
    paid: [CheckCircle2, "You're all set!", "Your plan is active. Thanks for choosing Beyond Boundaries ABA Scribe.", "text-emerald-600"],
    cancelled: [XCircle, "Checkout cancelled", "No charge was made. You can pick a plan anytime.", "text-slate-400"],
    failed: [XCircle, "Payment didn't go through", "Your checkout session expired. Please try again.", "text-red-500"],
    timeout: [Loader2, "Still processing", "Your payment is taking longer than usual. Check your plan page in a minute.", "text-amber-500"],
  }[state];
  const [Icon, title, text, cls] = view;

  return (
    <div className="max-w-md mx-auto mt-16 rounded-2xl border bg-white p-10 text-center fade-up" data-testid={`payment-result-${state}`}>
      <Icon className={`h-12 w-12 mx-auto ${cls}`} />
      <h1 className="mt-5 text-2xl font-bold">{title}</h1>
      <p className="mt-2 text-sm text-slate-500">{text}</p>
      {state !== "checking" && (
        <div className="mt-8 flex justify-center gap-3">
          <Button asChild variant="outline" className="rounded-full"><Link to="/billing" data-testid="payment-result-billing-link">Plan & team</Link></Button>
          {state === "paid" && <Button asChild className="rounded-full"><Link to="/notes/new" data-testid="payment-result-start-link">Write a note</Link></Button>}
        </div>
      )}
    </div>
  );
}
